'use client';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { ResultSectionSingleResultWithFetch, ResultsSection, ResultsSectionTitle } from '../../lotteries/ResultsSection/ResultsSection';
import { getParticipationStatusIcon } from '../../lotteries/utils/getParticipationStatusIcon/getParticipationStatusIcon';
import type { ParticipationState } from '../types';

type PurchaseResultsSectionProps = ComponentProps<'section'> & {
  state: ParticipationState;
  drawingId: string;
  date: Date;
};

/**
 * PurchaseResultsParticipationStatus component is responsible for displaying the purchase results participation status.
 * It displays the icon and the text of the participation status.
 * @param icon - The icon to display.
 * @param text - The text to display.
 * @returns The purchase results participation status component.
 */
function PurchaseResultsParticipationStatus({ icon, text, ...props }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2" {...props}>
      {icon}
      <p className="paragraph-s-strong">{text}</p>
    </div>
  );
}

/**
 * PurchaseResultsSection component is responsible for displaying the purchase results section.
 * It displays the results section title and the purchase results participation status.
 * It also displays the result section single result fetched based on the drawing id.
 * @param state - The state of the purchase.
 * @param date - The date of the purchase.
 * @param drawingId - The drawing id of the purchase.
 * @returns The purchase results section component.
 */
function PurchaseResultsSection({ state, date, drawingId, ...props }: PurchaseResultsSectionProps) {
  const t = useTranslations('lotteries.ticket_details.results_section');
  const { id, icon } = getParticipationStatusIcon(state, 'extra-small');
  return (
    <ResultsSection {...props}>
      <ResultsSectionTitle title={t('title')}>
        <PurchaseResultsParticipationStatus icon={icon} text={t(`states.${id}`)} data-track-status={id} />
      </ResultsSectionTitle>
      <ResultSectionSingleResultWithFetch drawingId={drawingId} date={date} hideLinkWhenNoResult hideStylingWhenNoResult data-track-container="Lottery Info" />
    </ResultsSection>
  );
}

export { PurchaseResultsParticipationStatus, PurchaseResultsSection };
